import { forwardRef, type ComponentProps, type ReactNode } from 'react'
import { Input as BaseInput } from '@base-ui/react/input'
import { twMerge } from 'tailwind-merge'
import { controlShellVariants, type ControlShellSize } from '../control-shell'
import { useFieldRequired } from '../field/nested/context'

/**
 * What only a text field adds to the shared shell: its placeholder colour, dimmed with the rest when
 * disabled. Ground, edge, corner and every state come from `controlShellVariants`, which
 * Select.Trigger and Combobox.Input share.
 */
export const inputStyles =
  'placeholder:text-foreground-placeholder is-disabled:placeholder:text-foreground-disabled relative flex w-full items-center'

type InputSize = ControlShellSize

interface InputProps extends Omit<ComponentProps<typeof BaseInput>, 'size'> {
  className?: string
  /**
   * Height, padding and radius, on Button's own `sm`/`md`/`lg`/`xl` heights so a field and a
   * button in one row line up. Density only: the type stays 13px at every size.
   */
  size?: InputSize
  /**
   * A node before the text, inside the box. Same name as Button's `left`. Setting it (or `right`)
   * puts the shell on a wrapper and `className` lands there; every other prop stays on the `<input>`.
   */
  left?: ReactNode
  /**
   * A node after the text, inside the box: an icon, or a ghost icon-only Button (one size down from the
   * input, see CONTROL_SHELL_ACTION_SIZE) such as a password's show/hide toggle. It needs its own
   * aria-label.
   */
  right?: ReactNode
}

/**
 * With a slot the shell moves to a wrapper and the input inside goes bare, so the edge, ring and
 * states belong to the whole box. The wrapper reads the input's state with `has-[]`, and the ring
 * only follows keyboard focus on the input, so pressing the slot's button never lights it.
 */
const wrappedShellStyles = [
  'has-[input:focus-visible]:border-[color:var(--focus-ring-color)] has-[input:focus-visible]:focus-ring',
  'has-[input:is([aria-invalid=true],[data-invalid])]:border-[color:var(--danger)] has-[input:is([aria-invalid=true],[data-invalid])]:hover:border-danger-hover',
  'has-[input:is([aria-invalid=true],[data-invalid]):focus-visible]:focus-ring-danger',
  'has-[input:is(:disabled,[data-disabled])]:cursor-not-allowed has-[input:is(:disabled,[data-disabled])]:bg-element-disabled has-[input:is(:disabled,[data-disabled])]:border-disabled has-[input:is(:disabled,[data-disabled])]:text-foreground-disabled',
].join(' ')

const bareInputStyles =
  'placeholder:text-foreground-placeholder disabled:placeholder:text-foreground-disabled h-full min-w-0 flex-1 bg-transparent text-inherit outline-none focus-visible:outline-none disabled:cursor-not-allowed'

/**
 * A bare glyph in a slot is 14px and secondary; a Button brings its own size. A leading slot pulls 2px
 * into the padding, so its glyph sits 7.5px inside the edge at every size; a trailing one pulls 6px,
 * the slack around a Button's own glyph.
 */
const slotStyles =
  'text-foreground-secondary flex shrink-0 items-center [&>svg:not([class*="size-"])]:size-3.5 [&>svg]:shrink-0'

const Input = forwardRef<HTMLInputElement, InputProps>(({ className, size, required, left, right, ...props }, ref) => {
  const isRequired = useFieldRequired(required)
  if (!left && !right) {
    return (
      <BaseInput
        ref={ref}
        required={isRequired}
        className={twMerge(inputStyles, controlShellVariants({ size }), className)}
        {...props}
      />
    )
  }
  return (
    <div
      className={twMerge(
        'relative flex w-full items-center',
        controlShellVariants({ size }),
        wrappedShellStyles,
        className,
      )}
    >
      {left && <span className={twMerge(slotStyles, '-ml-0.5')}>{left}</span>}
      <BaseInput ref={ref} required={isRequired} className={bareInputStyles} {...props} />
      {right && <span className={twMerge(slotStyles, '-mr-1.5')}>{right}</span>}
    </div>
  )
})

Input.displayName = 'Input'

export { Input }
export type { InputProps, InputSize }
